import React, { Suspense, useState, useCallback, useContext, useEffect } from 'react';
import {
  FileDropzone,
  FileUploadTypes,
  FileWithPreview,
  PageSpinner,
  ReactTableContextProvider,
  ReactTableContext,
  AddButton,
  IconButton,
  Select
} from '@td-client-frontend/common';
import { Download, RefreshCircleArrows } from '@td-client-frontend/icons';
import Modal from '../../components/Modal/NewModal';
import { useHistory } from 'react-router-dom';
import { useResellerJobsList } from '../../hooks/useResellerJobsList';
import { useHandleJobFiles } from '../../hooks/useJob';
import { FileError } from 'react-dropzone';
import { Button } from '../../components/Button';
import { JobTable } from './JobTable/JobTable';
import Pagination, { ItemsPerPageOption } from '../../components/Pagination';
import { TemplateSearchSelect, JobDateRange } from './components';
import moment from 'moment';

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'allErrors', label: 'Errors' },
  { value: 'allProcessing', label: 'Processing' },
  { value: 'pending', label: 'Pending' },
  { value: 'allCompleted', label: 'Done' },
  { value: 'stopped', label: 'Stopped' }
];

const defaultItemsPerPage = { label: '10', value: 10 };
const defaultPageNumber: number = 1;
const currentDate = new Date();

function fileValidation<T extends File>(file: T) {
  const errors: FileError[] = [];
  const regexp = new RegExp('^[A-Za-z]+_[0-9]{8}.zip$');
  if (!regexp.test(file.name)) {
    errors.push({
      code: 'invalid-file-name',
      message: 'Invalid file name. The file name should be in {TemplateName}_{MMDDYYYY}.zip format'
    });
  }
  return errors.length > 0 ? errors : null;
}

const defaultDate = {
  from: moment(currentDate).subtract(6, 'M').toDate(),
  to: moment(currentDate).toDate()
};

function ManageJobs() {
  const history = useHistory();

  const [currentPage, setCurrentPage] = useState<number>(defaultPageNumber);
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPageOption>(defaultItemsPerPage);

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isJobUploading, setIsJobUploading] = useState<boolean>(false);

  const [template, setTemplate] = useState<string>('');
  const [status, setStatus] = useState<string>(statusOptions[0].value);
  const [date, setDate] = useState<Record<string, Date>>(defaultDate);

  const [searchValue, setSearchValue] = useState<string>('');
  const [searchStatus, setSearchStatus] = useState<string>(statusOptions[0].value);
  const [startDateRangeValue, setStartDateRangeValue] = useState<Date>(date?.from);
  const [endDateRangeValue, setEndDateRangeValue] = useState<Date>(date?.to);

  const { onJobUpload, onZipDownload, onErrorsDownload, setAuditId } = useHandleJobFiles();
  const handleStatusChange = (value: string) => setSearchStatus(value);
  const dateRangeError = moment.utc(endDateRangeValue).diff(startDateRangeValue, 'month') > 6;

  const onReupload = (auditId: string) => {
    setAuditId(auditId);
    setShowUploadModal(true);
  };

  const handleModalClose = () => {
    setFiles([]);
    setIsJobUploading(false);
    setShowUploadModal(false);
  };

  const handleSearch = () => {
    setDate({
      from: startDateRangeValue,
      to: endDateRangeValue
    });
    setStatus(searchStatus);
    setTemplate(searchValue);
  };

  const resetSearchState = () => {
    setCurrentPage(defaultPageNumber);
    setDate(defaultDate);
    setStartDateRangeValue(defaultDate?.from);
    setEndDateRangeValue(defaultDate?.to);
    setStatus(statusOptions[0].value);
    setSearchStatus(statusOptions[0].value);
    setSearchValue('');
    setTemplate('');
  };

  const {
    state: { sortBy },
    handlers: { setSortBy }
  } = useContext(ReactTableContext);

  useEffect(() => {
    setSortBy([{ id: 'CreatedAt', desc: false }]);
  }, [setSortBy]);

  const { data, refetch: refreshJobs } = useResellerJobsList({
    date,
    template,
    status,
    sortBy,
    limit: itemsPerPage.value,
    skip: currentPage * itemsPerPage.value - itemsPerPage.value
  });

  const onReview = (id: string) => history.push(`/reseller/job-approval/${id}`);

  const uploadFiles = useCallback(async () => {
    setIsJobUploading(true);
    await onJobUpload(files[0]);
    handleModalClose();
  }, [files, onJobUpload]);

  const CustomButtons = () => {
    return (
      <div className="flex gap-4">
        <Button title="Cancel" kind="secondary" onClick={handleModalClose} />
        <Button
          title="Submit"
          type="submit"
          tooltip={!isJobUploading ? 'Please upload zipped csv files' : undefined}
          id="tooltip"
          disabled={!files.length || isJobUploading}
          onClick={uploadFiles}
        />
      </div>
    );
  };

  return (
    <>
      {/* content */}
      <div className="px-6">
        <div className="flex items-center justify-between w-full my-8">
          <h1 className="text-4xl font-effra font-bold">Reseller Jobs</h1>
        </div>
        <div className="flex items-center gap-4 w-full focus mb-0">
          <div className="grid grid-cols-10 gap-2 items-center">
            <div className="col-span-3">
              <TemplateSearchSelect
                name="template"
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                label="Select template"
                placeholder="Template name"
                error={!searchValue ? 'The field is required' : undefined}
                efficientValueLength={0}
                noSearchResultsMessage="No results found. Try adjusting your search criteria"
              />
            </div>
            <div className="col-span-2">
              <Select
                name="status"
                options={statusOptions}
                value={searchStatus}
                label="Status"
                placeholder=""
                onChange={handleStatusChange}
              />
            </div>
            <div className="col-span-3">
              <JobDateRange
                startDateRangeValue={startDateRangeValue}
                endDateRangeValue={endDateRangeValue}
                setStartDateRangeValue={setStartDateRangeValue}
                setEndDateRangeValue={setEndDateRangeValue}
                dateRangeError={dateRangeError}
              />
            </div>
            <div className="mb-1">
              <Button className="px-4 py-3" onClick={handleSearch} disabled={dateRangeError}>
                Search
              </Button>
            </div>
            <Button kind="default" className="whitespace-nowrap text-primary-500" onClick={resetSearchState}>
              Clear filters
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-end w-full mb-1">
          <AddButton
            onClick={() => setShowUploadModal(true)}
            label="Upload file"
            extraClass="mr-6"
            Icon={<Download className="transform rotate-180 fill-current text-primary-500" />}
          />
          <IconButton onClick={refreshJobs} label="Refresh">
            <RefreshCircleArrows />
          </IconButton>
        </div>
        <div className="flex flex-col w-full">
          {data?.records ? (
            <JobTable
              data={data?.records}
              onDownload={onZipDownload}
              onReview={onReview}
              onReupload={onReupload}
              getValidationErrors={onErrorsDownload}
            />
          ) : (
            <PageSpinner size={80} />
          )}
          {data?.totalPages > 1 && (
            <Pagination
              totalPages={data?.totalPages}
              onItemsPerPageChange={setItemsPerPage}
              selectedPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              isLoading={!data?.records}
            />
          )}
        </div>
      </div>
      {/* modals */}
      <Modal
        isOpen={showUploadModal}
        onClose={handleModalClose}
        header="Upload file"
        size="small"
        customFooterActions={{
          right: <CustomButtons />
        }}
      >
        <section className="p-6 font-bold w-full">
          <FileDropzone
            onRemove={setFiles}
            onUpload={setFiles}
            maxFileSizeMB={25}
            validator={fileValidation}
            mimeTypes={FileUploadTypes.ZIP}
          />
        </section>
      </Modal>
    </>
  );
}

export default function SuspenseWithLoader() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <ReactTableContextProvider>
        <ManageJobs />
      </ReactTableContextProvider>
    </Suspense>
  );
}
